import { useTranslation } from 'react-i18next'

import {
  ArrayFieldModel,
  CheckboxFieldModel,
  DateFieldModel,
  FieldType,
  FileFieldModel,
  FixedFileFieldModel,
  FixedPictureFieldModel,
  FixedTextFieldModel,
  FormField,
  FormModelStructure,
  FormModelStructureInput,
  HeaderPictureFieldModel,
  LocalFile,
  PictureFieldModel,
  RadioFieldModel,
  SectionFieldModel,
  SelectEditableFieldModel,
  SelectFieldModel,
  SignatureFieldModel,
  TextFieldModel,
  TitleFieldModel,
} from '@types'

import { useScrollTo } from '@utils/hooks/useScrollTo'

import { SCROLL_CONTAINER_FORM } from '@utils/constants/pdf-export-ids'

import { SubmitterFieldArray } from './components/SubmitterFieldArray'
import { SubmitterFieldCheckbox } from './components/SubmitterFieldCheckbox'
import { SubmitterFieldDate } from './components/SubmitterFieldDate'
import { SubmitterFieldFile } from './components/SubmitterFieldFile'
import { SubmitterFieldFixedFile } from './components/SubmitterFieldFixedFile'
import { SubmitterFieldFixedPicture } from './components/SubmitterFieldFixedPicture'
import { SubmitterFieldFixedText } from './components/SubmitterFieldFixedText'
import { SubmitterFieldHeaderPicture } from './components/SubmitterFieldHeaderPicture'
import { SubmitterFieldPicture } from './components/SubmitterFieldPicture'
import { SubmitterFieldRadio } from './components/SubmitterFieldRadio'
import { SubmitterFieldSection } from './components/SubmitterFieldSection'
import { SubmitterFieldSelect } from './components/SubmitterFieldSelect'
import { SubmitterFieldSelectEditable } from './components/SubmitterFieldSelectEditable'
import { SubmitterFieldSignature } from './components/SubmitterFieldSignature'
import { SubmitterFieldText } from './components/SubmitterFieldText'
import { SubmitterFieldTitle } from './components/SubmitterFieldTitle'

import { useFormSubmitter } from './hooks/useFormSubmitter.hook'

import { ButtonContainer, Field, FieldList, FormContainer, SubmitButton } from './FormSubmitter.styled'

type Mode = 'edit' | 'preview'

type FormSubmitterProps = {
  as?: 'form' | 'div'
  formModel: FormModelStructure
  mode: Mode
  onFirstInteraction?: () => void
  onChange: (formModel: FormModelStructureInput, localFiles: Record<string, LocalFile[]>) => void
  onSubmit?: (formModel: FormModelStructureInput, localFiles: Record<string, LocalFile[]>) => void
}

export const FormSubmitter: React.FC<FormSubmitterProps> = ({
  as = 'div',
  formModel,
  mode,
  onFirstInteraction,
  onSubmit,
  onChange,
}: FormSubmitterProps) => {
  const { t } = useTranslation()
  const scrollTo = useScrollTo()

  const {
    hasSubmit,
    isDisabled,
    filteredFields,
    showSubmitButton,
    handleSubmit,
    handleLocalFilesChange,
    handleFieldChange,
  } = useFormSubmitter({
    formModel,
    mode,
    onFirstInteraction,
    onChange,
    onSubmit,
  })

  const renderField = (field: FormField) => {
    const commonProps = {
      onChange: (value: Partial<FormField>) => handleFieldChange(field.id, value),
      disabled: isDisabled,
    }

    const optionsProps = {
      ...commonProps,
      onRedirect: scrollTo,
    }

    const fileProps = {
      ...commonProps,
      onLocalFilesChange: (files: LocalFile[]) => handleLocalFilesChange(field.id, files),
    }

    const arrayProps = {
      ...commonProps,
      scrollTo,
    }

    switch (field.type) {
      case FieldType.HeaderPicture:
        return <SubmitterFieldHeaderPicture field={field as HeaderPictureFieldModel} />
      case FieldType.Title:
        return <SubmitterFieldTitle field={field as TitleFieldModel} />
      case FieldType.Section:
        return <SubmitterFieldSection field={field as SectionFieldModel} />
      case FieldType.Array:
        return <SubmitterFieldArray {...arrayProps} field={field as ArrayFieldModel} />
      case FieldType.Text:
        return <SubmitterFieldText {...commonProps} field={field as TextFieldModel} />
      case FieldType.Date:
        return <SubmitterFieldDate {...commonProps} field={field as DateFieldModel} />
      case FieldType.Select:
        return <SubmitterFieldSelect {...optionsProps} field={field as SelectFieldModel} />
      case FieldType.SelectEditable:
        return <SubmitterFieldSelectEditable {...optionsProps} field={field as SelectEditableFieldModel} />
      case FieldType.Radio:
        return <SubmitterFieldRadio {...optionsProps} field={field as RadioFieldModel} />
      case FieldType.Signature:
        return <SubmitterFieldSignature {...commonProps} field={field as SignatureFieldModel} />
      case FieldType.Checkbox:
        return <SubmitterFieldCheckbox {...commonProps} field={field as CheckboxFieldModel} />
      case FieldType.File:
        return <SubmitterFieldFile {...fileProps} field={field as FileFieldModel} />
      case FieldType.Picture:
        return <SubmitterFieldPicture {...fileProps} field={field as PictureFieldModel} />
      case FieldType.FixedText:
        return <SubmitterFieldFixedText field={field as FixedTextFieldModel} />
      case FieldType.FixedFile:
        return <SubmitterFieldFixedFile field={field as FixedFileFieldModel} />
      case FieldType.FixedPicture:
        return <SubmitterFieldFixedPicture field={field as FixedPictureFieldModel} />
      default:
        return null
    }
  }

  return (
    <FormContainer onSubmit={handleSubmit} as={as} id={SCROLL_CONTAINER_FORM}>
      <FieldList>
        {filteredFields.map((field) => (
          <Field key={field.id}>{renderField(field)}</Field>
        ))}
      </FieldList>

      {showSubmitButton && onSubmit && (
        <ButtonContainer>
          <SubmitButton disabled={hasSubmit} type="submit">
            {t('modules_project-forms_components_FormSubmitter_save')}
          </SubmitButton>
        </ButtonContainer>
      )}
    </FormContainer>
  )
}
